import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { FC, Fragment, useCallback, useMemo } from 'react';

import dynamic from 'next/dynamic';
import { useRecoilValue } from 'recoil';
import { PATH, SectionsKey } from '~/constants/enum';
import { QUERY_GET_META_PRODUCT_DETAIL_SIMILAR_BY_SLUG, QUERY_PRODUCT_DETAIL_BY_SKU } from '~/data/product/gql';
import useRenderComponent from '~/hooks/useRenderComponent';
import useScrollTop from '~/hooks/useScrollTop';
import cartAtom from '~/services/cart/cart.atom';
import { Product, ProductVariant } from '~/services/product';
import { initializeApollo, initializeSSRApollo } from '~/utils/apollo-client';
import { transformProductItem } from '~/utils/transform';
import { getTheme } from '~/utils/theme';
import { getPageStruct, renderPage } from '~/utils/page';
import Layout from '~/layouts/ThemeLayout';
import { renderSectionNew } from '~/utils/pageTwo';
import React from 'react';
import { get } from 'lodash';
interface ProductDetailProps {
    product: Product;
    slug: string;
    faqData: any;
    dataMeta: any;
    defaultVariant: ProductVariant;
}

const pageStruct = getPageStruct(getTheme(), 'product-detail-similar');

const sections: any = pageStruct?.children
    ?.sort((a: any, b: any) => a.order - b.order)
    .map((item: any) => {
        const Section = renderSectionNew(item);

        return {
            id: item.id,
            order: item.order,
            dataKey: item.dataKey,
            component: !!Section ? <Section /> : null
        };
    });

const ProductDetail: FC<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ product, notFound }) => {
    useScrollTop();

    const sectionData = useMemo(() => {
        return {
            [SectionsKey.ProductDetailSimilar]: {
                product: product
            },
            [SectionsKey.SimilarProduct]: {
                sku: product?.sku
            }
        };
    }, [product]);

    return (
        <Fragment>
            <Layout>
                <div className="flex flex-col gap-2 lg:gap-8 min-h-screen mt-3">
                    {sections.map((section: any, index: number) => {
                        const Section = section.component;
                        return (
                            <React.Fragment key={`section-${index}`}>
                                {React.isValidElement(Section)
                                    ? React.cloneElement(Section as React.ReactElement, {
                                        ...get(sectionData, section.dataKey, {})
                                    })
                                    : Section}
                            </React.Fragment>
                        );
                    })}
                </div>
            </Layout>
        </Fragment>
    );
};

export default ProductDetail;

const ssrCache = new Map();
export const getServerSideProps: GetServerSideProps = async ({ params, res, query, req }) => {
    try {
        const apolloClient = initializeSSRApollo({
            httpLinkProps: { useGETForQueries: true }
        });

        const url_key = params?.slug as string;
        const b = Number(query?.b);
        const { data } = await apolloClient.query({
            query: QUERY_GET_META_PRODUCT_DETAIL_SIMILAR_BY_SLUG,
            variables: {
                param: url_key
            }
        });

        if (data?.products?.items?.length === 0) {
            const slug: string = (params?.slug as string) || '';
            const regex = /\b(\d{12})(-p)?\b/;
            const matching: any = slug.match(regex);

            const SKU = matching?.[1];

            if (!!SKU) {
                const { data: dataSKU } = await apolloClient.query({
                    query: QUERY_PRODUCT_DETAIL_BY_SKU,
                    variables: {
                        param: SKU
                    }
                });
                if (res && dataSKU?.products?.items?.length > 0) {
                    const { products } = dataSKU;
                    const correctURL = products?.items[0].url_key;
                    const querystring = require('querystring');
                    const tempQuery = { ...query };
                    delete tempQuery['slug'];
                    let newQuery = querystring.stringify(tempQuery);
                    if (!!newQuery) {
                        newQuery = '?' + newQuery;
                    }
                    res.writeHead(301, {
                        Location: `${PATH.ProductSimilarDetail.replace('[slug]', correctURL)}${newQuery}`
                    });
                    res.end();
                }
            } else {
                return {
                    notFound: true
                };
            }
        }
        if (!data?.products?.items?.length) {
            return {
                notFound: true
            };
        }

        const pageProps = {
            props: {
                product: transformProductItem(data?.products?.items?.[0]),
                slug: params?.slug,
                dataMeta: data?.products?.items?.[0] || {},
                b
            }
        };

        return pageProps;
    } catch (error) {
        console.log(error);
        return {
            notFound: true
        };
    }
};
